import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Wand2, RefreshCw, Copy, Check, AlertCircle } from 'lucide-react';
import appConfig from './config/appConfig';

const AIRequirementsDialog = ({
  isOpen,
  onClose,
  onCreateRequirements,
  levelId,
  functionalLevelPath,
  corporateAccount,
  currentProject,
  login_token,
  availableStatuses,
  availableCriticalities,
  availablePriorities
}) => {
  const [prompt, setPrompt] = useState('');
  const [generatedRequirements, setGeneratedRequirements] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [selectedRequirements, setSelectedRequirements] = useState(new Set());
  const [defaultStatus, setDefaultStatus] = useState('Created');
  const [defaultCriticality, setDefaultCriticality] = useState('');
  const [defaultPriority, setDefaultPriority] = useState('');

  const promptRef = useRef(null);

  // Dialog styles matching your existing pattern
  const dialogStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    dialog: {
      backgroundColor: 'white',
      borderRadius: '8px',
      width: '900px',
      maxWidth: '95vw',
      height: '90vh',
      maxHeight: '90vh',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    header: {
      backgroundColor: '#E0E7FF',
      textAlign: 'center',
      padding: '15px 20px',
      borderRadius: '8px 8px 0 0',
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#3730A3',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    content: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      maxHeight: 'calc(90vh - 140px)'
    },
    footer: {
      padding: '15px 20px',
      borderTop: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      borderRadius: '0 0 8px 8px',
      flexShrink: 0
    }
  };

  // Focus on prompt input when dialog opens
  useEffect(() => {
    if (isOpen && promptRef.current) {
      promptRef.current.focus();
    }
  }, [isOpen]);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setPrompt('');
      setGeneratedRequirements([]);
      setError('');
      setSelectedRequirements(new Set());


   // Set intelligent defaults similar to EnhancedLegacyCodeAnalysisDialog
    const defaultStatus = availableStatuses.includes('Created by AI') ? 'Created by AI' : (availableStatuses[0] || '');
    setDefaultStatus(defaultStatus);

    const defaultCriticality = availableCriticalities.includes('Must Have') ? 'Must Have' : (availableCriticalities[0] || '');
    setDefaultCriticality(defaultCriticality);

    const defaultPriority = availablePriorities.includes('High') ? 'High' : (availablePriorities[0] || '');
    setDefaultPriority(defaultPriority);

    }
  }, [isOpen, availableStatuses, availableCriticalities, availablePriorities]);

  // Sample prompts for inspiration
  const samplePrompts = [
    "Generate 5 requirements for user authentication and access control",
    "Create requirements for data backup and recovery functionality",
    "Generate security requirements for handling sensitive customer data",
    "Create performance requirements for web application response times",
    "Generate requirements for mobile app offline functionality"
  ];

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt to generate requirements');
      return;
    }

    if (!levelId) {
      setError('Please select a functional level before generating requirements');
      return;
    }

    setIsGenerating(true);
    setError('');
    setGeneratedRequirements([]);

    try {
      // Call the real API
      await generateRequirements();

    } catch (err) {
      setError(`Failed to generate requirements: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  // Real API call to generate requirements
  const generateRequirements = async () => {
    try {
      const response = await fetch(appConfig.getUrl('/generate_ai_requirements'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          prompt: prompt.trim(),
          functional_level: functionalLevelPath || 'Unknown',
          corporate_account: corporateAccount,
          project_id: currentProject,
          level_id: levelId,
          project_type: currentProject,
          existing_requirements_count: 0,
          // Pass actual system values for AI to use
          available_criticalities: availableCriticalities,
          available_priorities: availablePriorities,
          available_statuses: availableStatuses
        })
      });

      const data = await response.json();

      if (data.status === 'Success') {
        // Transform API response to match our UI format
        const transformedRequirements = data.requirements.map((req, index) => ({
          id: index + 1,
          description: req.description,
          criticality: req.criticality,
          priority: req.priority,
          rationale: req.rationale || ''
        }));

        setGeneratedRequirements(transformedRequirements);
        // Select all by default
        setSelectedRequirements(new Set(transformedRequirements.map(req => req.id)));

        if (transformedRequirements.length === 0) {
          setError('No requirements were generated. Please try a different prompt.');
        }
      } else {
        throw new Error(data.status_description || 'Failed to generate requirements');
      }
    } catch (error) {
      console.error('Error generating requirements:', error);
      throw error;
    }
  };

  const handleRequirementToggle = (reqId) => {
    const newSelected = new Set(selectedRequirements);
    if (newSelected.has(reqId)) {
      newSelected.delete(reqId);
    } else {
      newSelected.add(reqId);
    }
    setSelectedRequirements(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedRequirements.size === generatedRequirements.length) {
      setSelectedRequirements(new Set());
    } else {
      setSelectedRequirements(new Set(generatedRequirements.map(req => req.id)));
    }
  };

  const handleCreateSelected = async () => {
    const selectedReqs = generatedRequirements.filter(req =>
      selectedRequirements.has(req.id)
    );

    if (selectedReqs.length === 0) {
      setError('Please select at least one requirement to create');
      return;
    }

    // Call the parent component's create function
    onCreateRequirements(selectedReqs, {
      status: defaultStatus,
      criticality: defaultCriticality,
      priority: defaultPriority
    });
  };

  const copyPrompt = (samplePrompt) => {
    setPrompt(samplePrompt);
    if (promptRef.current) {
      promptRef.current.focus();
    }
  };

  if (!isOpen) return null;

  return (
    <div style={dialogStyles.overlay}>
      <div style={dialogStyles.dialog}>
        {/* Header */}
        <div style={dialogStyles.header}>
          <Sparkles className="mr-3" size={24} />
          AI Requirements Generator
        </div>

        {/* Content */}
        <div style={dialogStyles.content}>
          {/* Functional Level Info */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <h4 className="font-semibold text-blue-800 mb-1">Target Functional Level:</h4>
            <p className="text-sm text-blue-700">
              {functionalLevelPath || 'Please select a functional level first'}
            </p>
          </div>

          {/* Prompt Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Describe what requirements you need:
            </label>
            <textarea
              ref={promptRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Example: Generate 5 security requirements for user authentication including password policies, session management, and access controls..."
              className="w-full p-3 border border-gray-300 rounded-lg h-24 resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  handleGenerate();
                }
              }}
            />
            <p className="text-xs text-gray-500 mt-1">
              Tip: Press Ctrl+Enter to generate, or be specific about quantity, domain, and requirements type
            </p>
          </div>

          {/* Sample Prompts */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Sample Prompts:</h4>
            <div className="flex flex-wrap gap-2">
              {samplePrompts.map((sample, index) => (
                <button
                  key={index}
                  onClick={() => copyPrompt(sample)}
                  className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded border text-left transition-colors"
                  title="Click to use this prompt"
                >
                  {sample}
                </button>
              ))}
            </div>
          </div>

          {/* Default Settings */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-700 mb-3">Default Settings for Generated Requirements:</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Status:</label>
                <select
                  value={defaultStatus}
                  onChange={(e) => setDefaultStatus(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded text-sm"
                >
                  {availableStatuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Criticality:</label>
                <select
                  value={defaultCriticality}
                  onChange={(e) => setDefaultCriticality(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded text-sm"
                >
                  <option value="">Select criticality</option>
                  {availableCriticalities.map(crit => (
                    <option key={crit} value={crit}>{crit}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Priority:</label>
                <select
                  value={defaultPriority}
                  onChange={(e) => setDefaultPriority(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded text-sm"
                >
                  <option value="">Select priority</option>
                  {availablePriorities.map(priority => (
                    <option key={priority} value={priority}>{priority}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <div className="mb-6 text-center">
            <button
              onClick={handleGenerate}
              disabled={isGenerating || !prompt.trim() || !levelId}
              className={`px-6 py-3 rounded-lg font-medium flex items-center justify-center mx-auto transition-all ${
                isGenerating || !prompt.trim() || !levelId
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:shadow-xl'
              }`}
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="animate-spin mr-2" size={18} />
                  Generating Requirements...
                </>
              ) : (
                <>
                  <Wand2 className="mr-2" size={18} />
                  Generate Requirements
                </>
              )}
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-center">
              <AlertCircle className="text-red-500 mr-2" size={16} />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}

          {/* Enhanced Generated Requirements with rationale */}
          {generatedRequirements.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-green-50 border-b px-4 py-3 flex items-center justify-between">
                <h4 className="font-medium text-green-800">
                  Generated Requirements ({generatedRequirements.length})
                </h4>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setGeneratedRequirements([]);
                      setSelectedRequirements(new Set());
                    }}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={handleSelectAll}
                    className="text-sm text-green-600 hover:text-green-800"
                  >
                    {selectedRequirements.size === generatedRequirements.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              </div>

              <div className="max-h-80 overflow-y-auto">
                {generatedRequirements.map((req) => (
                  <div key={req.id} className="border-b last:border-b-0 p-4 hover:bg-gray-50">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        checked={selectedRequirements.has(req.id)}
                        onChange={() => handleRequirementToggle(req.id)}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1">

                        {/* Edit requirement inline */}
                        <div className="mt-2">
                          <textarea
                            value={req.description}
                            onChange={(e) => {
                              const updated = generatedRequirements.map(r =>
                                r.id === req.id ? { ...r, description: e.target.value } : r
                              );
                              setGeneratedRequirements(updated);
                            }}
                            className="w-full text-xs p-2 border border-gray-600 bg-gray-100 rounded resize-none"
                            rows={2}
                            placeholder="Edit requirement description..."
                          />
                        </div>

                        {/* Rationale if available */}
                        {req.rationale && (
                          <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                            <strong>Rationale:</strong> {req.rationale}
                          </div>
                        )}


                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={dialogStyles.footer}>
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {generatedRequirements.length > 0 && (
                <span>{selectedRequirements.size} of {generatedRequirements.length} requirements selected</span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              {generatedRequirements.length > 0 && (
                <button
                  onClick={handleCreateSelected}
                  disabled={selectedRequirements.size === 0}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    selectedRequirements.size === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  Create Selected Requirements ({selectedRequirements.size})
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIRequirementsDialog;